import { Helmet } from 'react-helmet-async'
import { Link, useLocation } from 'react-router-dom'
import { type ReactNode, useEffect } from 'react'
import { PrivacySettingsLink } from '../CookieConsent'
import { formatPageTitle } from '../../lib/seo'
import { skipToMain } from './skipToMain'

interface PageShellProps {
  title: string
  description: string
  ogImage?: string
  schema?: object | object[]
  noindex?: boolean
  canonical?: string
  children: ReactNode
}

const SITE = 'https://acesense.io'
const DEFAULT_OG = `${SITE}/og-image.png`
const primaryLinks = [
  ['/features', 'Features'],
  ['/how-it-works', 'How it works'],
  ['/compare', 'Compare'],
  ['/pricing', 'Pricing'],
  ['/blog', 'Blog'],
] as const

export function PageShell({
  title,
  description,
  ogImage,
  schema,
  noindex,
  canonical,
  children,
}: PageShellProps) {
  const { pathname } = useLocation()
  const url = `${SITE}${canonical ?? pathname}`
  const og = ogImage ?? DEFAULT_OG
  const fullTitle = formatPageTitle(title)

  // Each marketing route is its own document for analytics/scroll-position;
  // make sure we land at the top instead of inheriting the previous route's
  // scroll offset.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return (
    <>
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="AceSense" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={og} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={og} />
        <meta
          name="robots"
          content={noindex ? 'noindex, nofollow' : 'index, follow'}
        />
        {schema && (
          <script type="application/ld+json">
            {JSON.stringify(Array.isArray(schema) ? schema : [schema])}
          </script>
        )}
      </Helmet>
      <a className="skip-link" href="#main-content" onClick={skipToMain}>
        Skip to main content
      </a>
      <Header />
      <main id="main-content" className="page-main" tabIndex={-1}>{children}</main>
      <Footer />
    </>
  )
}

function Header() {
  return (
    <header className="page-header">
      <div className="page-header-inner">
        <Link to="/" className="page-logo" aria-label="AceSense home">
          <img src="/logo.svg" alt="AceSense" width={120} height={28} />
        </Link>
        <nav className="page-nav" aria-label="Primary">
          {primaryLinks.map(([to, label]) => <Link key={to} to={to}>{label}</Link>)}
        </nav>
        <details className="page-nav-menu">
          <summary>Menu</summary>
          <nav aria-label="Mobile primary">
            {primaryLinks.map(([to, label]) => <Link key={to} to={to}>{label}</Link>)}
          </nav>
        </details>
      </div>
    </header>
  )
}

export function Footer() {
  return (
    <footer className="page-footer">
      <div className="page-footer-inner">
        <div className="footer-col">
          <h2>Product</h2>
          <Link to="/features">Features</Link>
          <Link to="/how-it-works">How it works</Link>
          <Link to="/accuracy">Accuracy</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/changelog">Changelog</Link>
        </div>
        <div className="footer-col">
          <h2>Compare</h2>
          <Link to="/compare/swingvision">vs SwingVision</Link>
          <Link to="/compare/pb-vision">vs PB Vision</Link>
          <Link to="/compare/baseline-vision">vs Baseline Vision</Link>
          <Link to="/compare/playsight">vs PlaySight</Link>
          <Link to="/compare/onform">vs OnForm</Link>
          <Link to="/compare/topcourt">vs TopCourt</Link>
          <Link to="/compare">All comparisons</Link>
        </div>
        <div className="footer-col">
          <h2>Resources</h2>
          <Link to="/blog">Blog</Link>
          <Link to="/how-to/film-your-tennis-match">How to film a match</Link>
          <Link to="/faq">FAQ</Link>
          <Link to="/examples">Examples</Link>
          <Link to="/android">Android</Link>
          <Link to="/ios">iOS</Link>
        </div>
        <div className="footer-col">
          <h2>Company</h2>
          <Link to="/about">About</Link>
          <Link to="/founder">Founder</Link>
          <a href="/contact">Contact</a>
          <Link to="/support">Support</Link>
          <Link to="/delete-account">Delete account</Link>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/accessibility">Accessibility</a>
          <a href="/privacy#do-not-sell">Do Not Sell or Share My Info</a>
          <PrivacySettingsLink />
        </div>
      </div>
      <div className="page-footer-base">
        <span>© AceSense. AI tennis video analysis</span>
        <div className="footer-social" style={{ display: 'flex', gap: '1rem', borderTop: 'none', paddingTop: 0, marginBottom: 0 }}>
          <a href="https://twitter.com/acesense_ai" target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label="AceSense on X (Twitter)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.713 5.984L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
            </svg>
          </a>
          <a href="https://linkedin.com/company/acesense" target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label="AceSense on LinkedIn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </a>
        </div>
        <span>Built in Europe · Hosted in europe-west1</span>
      </div>
    </footer>
  )
}
